import { ActivatedRouteSnapshot, NavigationExtras, Router } from '@angular/router';
import {
  APPRENTICESHIP_PRODUCT_CODES,
  featureMapping,
  isProductCode,
  Product,
  PRODUCT_OFFERING_ULTIMATE_PRODUCTS,
  PRODUCT_SORTING_ORDER,
  ProductCode,
  ProductFeature,
  ProductOfferingBenefit,
  ProductOfferingFeature,
  ProductOfferingFeatureDictionary,
  ProductOfferingPlatform,
  ProductPlatformGroup,
  ProductPlatformOther,
} from '@mkp/shared/models';
export const featurePlanSeparator = '/';

export const productOfferingPlatformGroups = Object.values(ProductPlatformGroup);

export const removeApprenticeshipProducts = (products: Product[]): Product[] => {
  return products.filter((product) => !APPRENTICESHIP_PRODUCT_CODES.includes(product.code));
};

type FilterCmsProductsInput = [Product[], ProductCode[]];

export const filterCmsProducts = ([products, removedCmsProductCodes]: FilterCmsProductsInput) => {
  return removedCmsProductCodes.length
    ? products.filter(({ code }) => !removedCmsProductCodes.includes(code))
    : products;
};

export const getFeaturesFromProducts = (products: Product[]): ProductOfferingBenefit[] => {
  const featuresSet = new Set<ProductOfferingBenefit>();

  products.forEach(({ features }: Product) => {
    // ignore the calendar feature, it is only used for the label on the product card
    features
      .filter(({ id }) => !id.startsWith('calendar'))
      .map(({ id }: ProductFeature) => {
        const [feature] = id.split(featurePlanSeparator, 2);
        featuresSet.add(feature as ProductOfferingBenefit);
      });
  });

  return Array.from(featuresSet);
};

/**
 * @deprecated
 * @param platform
 * @param platformGroups
 */
export const getSwitchedPlatformFrom = (
  platform: ProductPlatformGroup,
  platformGroups = productOfferingPlatformGroups
): ProductPlatformGroup => {
  const indexOfCurrentPlatform = platformGroups.indexOf(platform);
  return [...platformGroups, platformGroups[0]][indexOfCurrentPlatform + 1] as ProductPlatformGroup;
};

export const isFeatureABenefit = (
  feature: ProductOfferingFeature
): feature is ProductOfferingBenefit => {
  return Object.values(ProductOfferingBenefit).includes(feature as ProductOfferingBenefit);
};

export const isFeatureAPlatform = (
  feature: ProductOfferingFeature
): feature is ProductOfferingPlatform => {
  return [...Object.values(ProductPlatformGroup), ...Object.values(ProductPlatformOther)].includes(
    feature as ProductOfferingPlatform
  );
};

export const isProductOfferingPlatformGroup = (
  productOffering: unknown
): productOffering is ProductPlatformGroup =>
  Object.values(ProductPlatformGroup).some((platformGroup) => productOffering === platformGroup);

export const getPlatformsFromFeatures = (
  features: ProductOfferingFeature[],
  activePlatformGroup: ProductPlatformGroup
): ProductOfferingPlatform[] => {
  const platforms = features.filter(isFeatureAPlatform);
  return moveActivePlatformToBeginning(platforms, activePlatformGroup);
};

export const getBenefitsFromFeatures = (
  features: ProductOfferingFeature[]
): ProductOfferingBenefit[] => {
  return features.filter(isFeatureABenefit);
};

export const orderFeatures = (
  features: ProductOfferingFeature[],
  fmapping: ProductOfferingFeatureDictionary = featureMapping
): typeof features => {
  return features.sort((a, b) => {
    const {
      [a]: { order: orderA },
      [b]: { order: orderB },
    } = fmapping;
    return orderA - orderB;
  });
};

export const moveActivePlatformToBeginning = (
  platforms: ProductOfferingPlatform[],
  activePlatformGroup: ProductPlatformGroup
): ProductOfferingPlatform[] => {
  return platforms.reduce(
    (acc, platform) =>
      isProductOfferingPlatformGroup(platform) && platform === activePlatformGroup
        ? [platform, ...acc]
        : [...acc, platform],
    [] as ProductOfferingPlatform[]
  );
};

export const isProductOfferingUltimate = (code: ProductCode): boolean =>
  PRODUCT_OFFERING_ULTIMATE_PRODUCTS.includes(code);

export const getProductCodeFromRoute = (
  route: ActivatedRouteSnapshot,
  router: Router
): Product['code'] | undefined =>
  // productCode can come from
  // 1. queryParams (marketing direct URL CDP-80)
  // 2. params (credit management checkout page CDA-1022)
  // 3. navigation.extras (when selecting a product on step 3)
  // we need to check both current and last successful navigation
  // because, when used in guard, the lastSuccessfulNavigation is not available
  getAndVerifyProductCode(route.queryParamMap.get('productCode')) ??
  getAndVerifyProductCode(route.paramMap.get('type')) ??
  getAndVerifyProductCode(router.currentNavigation()?.extras?.state?.['productCode']) ??
  getAndVerifyProductCode(router.lastSuccessfulNavigation()?.extras?.state?.['productCode']);

export const getProductCodeForRoute = (
  productCode: Product['code'],
  quantity = 1
): NavigationExtras => ({
  state: { productCode, quantity },
});

export const readInitialQuantityFromRoute = (router: Router): number => {
  const fromCurrent = router.currentNavigation()?.extras?.state?.['quantity'];
  const fromLast = router.lastSuccessfulNavigation()?.extras?.state?.['quantity'];
  const raw = fromCurrent ?? fromLast;
  const parsed = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const getAndVerifyProductCode = (productCode: string | null): Product['code'] | undefined =>
  isProductCode(productCode) ? (productCode as Product['code']) : undefined;

const productCodeSortingMapDefault = Object.values(ProductCode).reduce<Record<string, number>>(
  (acc, productCode, index) => ({
    ...acc,
    [productCode.toString()]: index,
  }),
  {}
);

/**
 * @deprecated Use PRODUCT_SORTING_ORDER from @mkp/shared/models instead
 * This is kept for backward compatibility only
 */
export const productOfferingSortingMap = PRODUCT_SORTING_ORDER;

export const sortProducts = (
  p1: Product,
  p2: Product,
  sortingMap?: Record<string, number>
): number =>
  sortingMap
    ? sortingMap[p1.code] - sortingMap[p2.code]
    : productCodeSortingMapDefault[p1.code] - productCodeSortingMapDefault[p2.code];
